import { normalizeInvoiceRole } from './invoiceLedger';
import type { SurveyDeltaClassification } from './surveyDelta';

export const FINAL_REOPEN_REQUIRED =
  'Final Survey is complete. Reopen Final Survey before editing.';

export const FINAL_SURVEY_HISTORICAL_AFTER_BALANCE =
  'Final Survey is historical. The Final Balance has been issued. Subsequent adjustments use a Variation or Credit invoice.';

export const FINAL_PENDING_REVIEW_FROZEN =
  'Final Survey has an unresolved review. Reopen Final Survey to edit, or Review Changes to decide.';

export const FINAL_ALREADY_DECIDED =
  'Final Survey is already complete. Reopen Final Survey to start another working cycle.';

export const FINAL_REVIEW_SUPERSEDED =
  'This review was superseded when Final Survey was reopened.';

export const FINAL_REVIEW_VERSION_MISMATCH =
  'This review does not belong to the current completed Final Survey.';

export const FINAL_REOPEN_STANDARD_BODY =
  'This makes the completed Final editable again; your next save replaces it. Anything already sent to the customer is kept. You will need to Mark Complete again (full delta vs the original First transmission) before any further customer reapproval.';

export const FINAL_REOPEN_IN_FLIGHT_ALERT =
  'The customer still has an outstanding Final Survey approval request. Reopening withdraws that request and nullifies their current link — they will no longer be able to accept it.';

export const FINAL_REOPEN_IN_FLIGHT_BODY =
  'This makes the completed Final editable again; your next save replaces it. Anything already sent to the customer is kept. You will need to Mark Complete again and send a new approval if the customer still needs to confirm.';

export const FINAL_IN_FLIGHT_TAB_ALERT =
  'A Final Survey approval request is already with the customer. They can still accept the current link until you reopen (which withdraws it) or retransmit (which replaces it).';

export type FinalSurveyLifecycleDelta = {
  id?: string | null;
  classification?: SurveyDeltaClassification;
  finalVersionId?: string | null;
  decidedAt?: string | null;
  customerAcceptedAt?: string | null;
  reapprovalTransmissionVersionId?: string | null;
  reapprovalShareLinkId?: string | null;
  supersededAt?: string | null;
  completedAt?: string | null;
};

export type FinalSurveyLifecycleSurvey = {
  kind?: string | null;
  completionStatus?: string | null;
  documentVersionId?: string | null;
  reopenedAt?: string | null;
};

/**
 * Reopen keeps the same Final record, so a matching record id no longer proves
 * a review belongs to the current cycle. A review completed before the last
 * reopen is stale even if its supersede write was missed.
 */
function reviewPredatesReopen(
  survey: FinalSurveyLifecycleSurvey | null | undefined,
  delta: FinalSurveyLifecycleDelta,
): boolean {
  const reopened = Date.parse(String(survey?.reopenedAt || ''));
  const completed = Date.parse(String(delta.completedAt || ''));
  if (!Number.isFinite(reopened) || !Number.isFinite(completed)) return false;
  return completed < reopened;
}

export function isInFlightFinalReapproval(
  delta: FinalSurveyLifecycleDelta | null | undefined,
): boolean {
  if (!delta) return false;
  if (String(delta.supersededAt || '').trim()) return false;
  if (String(delta.customerAcceptedAt || '').trim()) return false;
  if (delta.classification !== 'customer_reapproval_required') return false;
  return !!String(delta.reapprovalTransmissionVersionId || '').trim();
}

/** Unaccepted current deltas are abandoned on Reopen — including a sent-but-unanswered Final. */
export function shouldSupersedeDeltaOnFinalReopen(
  delta: FinalSurveyLifecycleDelta | null | undefined,
): boolean {
  if (!delta) return false;
  if (String(delta.supersededAt || '').trim()) return false;
  return !String(delta.customerAcceptedAt || '').trim();
}

export type FinalBalanceInvoice = {
  id?: string | null;
  role?: string | null;
  invoiceRole?: string | null;
  status?: string | null;
};

export type FinalBalanceScheduleStage = {
  kind?: string | null;
  status?: string | null;
  invoiceId?: string | null;
};

function isIssuedInvoice(invoice: FinalBalanceInvoice): boolean {
  const status = String(invoice.status || '').trim().toLowerCase();
  return status !== '' && status !== 'void' && status !== 'draft' && status !== 'rejected';
}

/**
 * Final Balance is the remainder invoice — simple-path works, or a schedule
 * stage whose kind is `final`. Lock starts on issuance (not draft/void/rejected).
 * Interim/Progress works invoices do not count. A final stage marked issued
 * without a bound issued invoice does not lock.
 */
export function isFinalBalanceIssued(args: {
  invoices?: FinalBalanceInvoice[] | null;
  scheduleStages?: FinalBalanceScheduleStage[] | null;
}): boolean {
  const invoices = args.invoices || [];
  const stages = args.scheduleStages || [];
  if (stages.length > 0) {
    return stages.some((stage) => {
      if (String(stage.kind || '').trim().toLowerCase() !== 'final') return false;
      const invoiceId = String(stage.invoiceId || '').trim();
      if (!invoiceId) return false;
      const bound = invoices.find((invoice) => String(invoice.id || '') === invoiceId);
      return !!bound && isIssuedInvoice(bound);
    });
  }
  return invoices.some((invoice) => (
    isIssuedInvoice(invoice)
    && normalizeInvoiceRole(invoice.invoiceRole ?? invoice.role) === 'works'
  ));
}

export function canReopenFinalSurvey(args: {
  invoices?: FinalBalanceInvoice[] | null;
  scheduleStages?: FinalBalanceScheduleStage[] | null;
}): boolean {
  return !isFinalBalanceIssued(args);
}

export function assertFinalSurveyReopenAllowed(args: {
  invoices?: FinalBalanceInvoice[] | null;
  scheduleStages?: FinalBalanceScheduleStage[] | null;
}): void {
  if (isFinalBalanceIssued(args)) {
    throw new Error(FINAL_SURVEY_HISTORICAL_AFTER_BALANCE);
  }
}

export function finalReopenConfirmCopy(inFlight: boolean): {
  title: string;
  alert: string | null;
  body: string;
  confirmLabel: string;
  confirmColor: 'warning' | 'danger';
} {
  if (inFlight) {
    return {
      title: 'Withdraw customer approval and reopen?',
      alert: FINAL_REOPEN_IN_FLIGHT_ALERT,
      body: FINAL_REOPEN_IN_FLIGHT_BODY,
      confirmLabel: 'Withdraw request and reopen',
      confirmColor: 'danger',
    };
  }
  return {
    title: 'Reopen Final Survey',
    alert: null,
    body: FINAL_REOPEN_STANDARD_BODY,
    confirmLabel: 'Reopen',
    confirmColor: 'warning',
  };
}

export function isUnresolvedPendingReviewDelta(
  delta: FinalSurveyLifecycleDelta | null | undefined,
): boolean {
  if (!delta) return false;
  if (String(delta.supersededAt || '').trim()) return false;
  if (String(delta.decidedAt || '').trim()) return false;
  const classification = delta.classification;
  return classification === 'pending_review' || classification == null;
}

export function isCurrentFinalReview(
  survey: FinalSurveyLifecycleSurvey | null | undefined,
  delta: FinalSurveyLifecycleDelta | null | undefined,
): boolean {
  if (!survey || !delta) return false;
  if (String(survey.kind || '').trim().toLowerCase() !== 'final') return false;
  if (String(survey.completionStatus || '').trim().toLowerCase() !== 'complete') return false;
  if (!isUnresolvedPendingReviewDelta(delta)) return false;
  if (reviewPredatesReopen(survey, delta)) return false;
  const surveyVersion = String(survey.documentVersionId || '').trim();
  const deltaVersion = String(delta.finalVersionId || '').trim();
  if (surveyVersion && deltaVersion && surveyVersion !== deltaVersion) return false;
  return true;
}

export function isFinalSurveyFrozen(args: {
  kind?: string | null;
  completionStatus?: string | null;
  documentVersionId?: string | null;
  currentDelta?: FinalSurveyLifecycleDelta | null;
}): boolean {
  if (String(args.kind || '').trim().toLowerCase() !== 'final') return false;
  if (String(args.completionStatus || '').trim().toLowerCase() === 'complete') return true;
  return isUnresolvedPendingReviewDelta(args.currentDelta);
}

export function finalSurveyMutationGuard(args: {
  kind?: string | null;
  completionStatus?: string | null;
  documentVersionId?: string | null;
  currentDelta?: FinalSurveyLifecycleDelta | null;
}): { ok: true } | { ok: false; reason: string } {
  if (String(args.kind || '').trim().toLowerCase() !== 'final') return { ok: true };
  if (String(args.completionStatus || '').trim().toLowerCase() === 'complete') {
    return { ok: false, reason: FINAL_REOPEN_REQUIRED };
  }
  if (isUnresolvedPendingReviewDelta(args.currentDelta)) {
    return { ok: false, reason: FINAL_PENDING_REVIEW_FROZEN };
  }
  return { ok: true };
}

export function assertFinalSurveyMutable(args: {
  kind?: string | null;
  completionStatus?: string | null;
  documentVersionId?: string | null;
  currentDelta?: FinalSurveyLifecycleDelta | null;
}): void {
  const guard = finalSurveyMutationGuard(args);
  if (!guard.ok) throw new Error(guard.reason);
}

export type MarkFinalCompleteAction =
  | { type: 'reject'; reason: string }
  | { type: 'return_existing_pending'; deltaId: string }
  | { type: 'create_pending_vs_first' };

export function resolveMarkFinalCompleteAction(args: {
  kind?: string | null;
  completionStatus?: string | null;
  documentVersionId?: string | null;
  currentDelta?: FinalSurveyLifecycleDelta | null;
}): MarkFinalCompleteAction {
  if (String(args.kind || '').trim().toLowerCase() !== 'final') {
    return { type: 'reject', reason: 'Only Final Survey can be marked complete.' };
  }
  const complete = String(args.completionStatus || '').trim().toLowerCase() === 'complete';
  const current = args.currentDelta;
  if (complete || isUnresolvedPendingReviewDelta(current)) {
    if (isUnresolvedPendingReviewDelta(current) && current?.id) {
      return { type: 'return_existing_pending', deltaId: String(current.id) };
    }
    if (complete) {
      return { type: 'reject', reason: FINAL_ALREADY_DECIDED };
    }
  }
  return { type: 'create_pending_vs_first' };
}

export type DecideFinalDeltaAction =
  | { type: 'reject'; reason: string }
  | { type: 'decide' };

export function resolveDecideFinalDeltaAction(args: {
  survey: FinalSurveyLifecycleSurvey | null | undefined;
  delta: FinalSurveyLifecycleDelta | null | undefined;
}): DecideFinalDeltaAction {
  const delta = args.delta;
  if (!delta) return { type: 'reject', reason: 'Final Survey delta not found.' };
  if (String(delta.supersededAt || '').trim()) {
    return { type: 'reject', reason: FINAL_REVIEW_SUPERSEDED };
  }
  if (delta.classification !== 'pending_review' && delta.classification != null) {
    return { type: 'reject', reason: 'This Final Survey review has already been decided.' };
  }
  const survey = args.survey;
  if (String(survey?.completionStatus || '').trim().toLowerCase() !== 'complete') {
    return { type: 'reject', reason: FINAL_REOPEN_REQUIRED };
  }
  if (reviewPredatesReopen(survey, delta)) {
    return { type: 'reject', reason: FINAL_REVIEW_SUPERSEDED };
  }
  const surveyVersion = String(survey?.documentVersionId || '').trim();
  const deltaVersion = String(delta.finalVersionId || '').trim();
  if (surveyVersion && deltaVersion && surveyVersion !== deltaVersion) {
    return { type: 'reject', reason: FINAL_REVIEW_VERSION_MISMATCH };
  }
  return { type: 'decide' };
}

/** Closing Review Changes is display-only. Lifecycle state is unchanged. */
export function applyCloseReviewChangesModal<T>(state: T): T {
  return state;
}
